import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { MESSAGING_TEMPLATES_REFERENCE_DATA_TYPES, MessagingTemplatesReferenceData } from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getMessagingTemplatesReduxRequest } from "@redux-requests/messaging-templates";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { AddMessagingTemplateModal } from "@containers/modals/add-messaging-template-modal/add-messaging-template-modal";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { MessagingTemplatesTableContainer } from "./messaging-templates-table-container/messaging-templates-table-container";
import { MESSAGING_TEMPLATE_TYPE_OPTIONS } from "@constants/messaging-template-type-options";
import { getActiveReferenceData } from "@utils/data-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getMessagingTemplatesDataState } from "@redux/selectors/messaging-templates-selectors";

export const MessagingTemplatesContainer = () => {
    const modalContext = useModalContext()

    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { messagingTemplates: messagingTemplatesTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingMessagingTemplates,
        messagingTemplates
    } = useSelector(getMessagingTemplatesDataState);
    const referenceData = useReferenceDataSelector<MessagingTemplatesReferenceData>(MESSAGING_TEMPLATES_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadMessagingTemplates, setShouldLoadMessagingTemplates] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.messagingTemplates && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                MESSAGING_TEMPLATES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.MESSAGING_TEMPLATES
            );
        }
    }, [referenceData.isLoading.messagingTemplates, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnRelyingParties
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnRelyingParties
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingMessagingTemplates && shouldLoadMessagingTemplates) {
            setShouldLoadMessagingTemplates(false);
            getMessagingTemplatesReduxRequest(dispatch)(referenceData.axnCompanies, referenceData.axnRelyingParties);
        }
    }, [isReferenceDataPresent, isLoadingMessagingTemplates, shouldLoadMessagingTemplates]);

    const {
        axnCompanies: activeAxnCompanies,
        axnRelyingParties: activeAxnRelyingParties
    } = useMemo(() => {
        if (isReferenceDataPresent) {
            const { axnCompanies, axnRelyingParties } = referenceData;

            return getActiveReferenceData({
                axnCompanies,
                axnRelyingParties
            });
        }

        return {
            axnCompanies: [],
            axnRelyingParties: []
        };
    }, [isReferenceDataPresent, referenceData.axnCompanies, referenceData.axnRelyingParties]);
    
    const renderAddMessagingTemplate = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddMessagingTemplateModal
                session={session}
                isVericationServiceDetailsPage={false}
                serviceOrganizationId={null}
                serviceRelyingPartyId={null}
                onModalClose={onModalClose}
                availableMessagingTemplateTypeOptions={MESSAGING_TEMPLATE_TYPE_OPTIONS}
                axnCompanies={activeAxnCompanies}
                axnRelyingParties={activeAxnRelyingParties}
                addToReferenceData={false}
            />
        );
    }, [activeAxnCompanies, activeAxnRelyingParties]);

    const isLoading = useMemo(() => {
        return (
            isLoadingMessagingTemplates ||
            referenceData.isLoading.messagingTemplates
        );
    }, [
        isLoadingMessagingTemplates,
        referenceData.isLoading.messagingTemplates
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            messagingTemplates &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadMessagingTemplates
        );
    }, [isLoading, messagingTemplates, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadMessagingTemplates]);


    const headerButton = useMemo(() => {
        if (session.userPermissions.canCreateMessagingTemplates) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddMessagingTemplate,
                    renderFunction: renderAddMessagingTemplate
                });
            };
            return (
                <ButtonWithIcon
                    id="add-messaging-template-button"
                    title={resources.pages.messagingTemplates.headerButton}
                    text={resources.pages.messagingTemplates.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        }

        return null;
    }, [isLoading, isLoaded, session, renderAddMessagingTemplate]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.messagingTemplates.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <MessagingTemplatesTableContainer
                        messagingTemplates={messagingTemplates}
                        tableState={messagingTemplatesTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};